import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { keepOpenForToasts } from "@/components/ui/toast-outside";
import { cn } from "@/lib/utils";
import { useBackClosable } from "@/lib/back-stack";

/** A dialog the system back closes (`lib/back-stack.ts`); `Sheet` is the same root. */
export function Dialog(props: React.ComponentProps<typeof DialogPrimitive.Root>) {
  const control = useBackClosable(props);
  return <DialogPrimitive.Root {...props} {...control} />;
}
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogPortal = DialogPrimitive.Portal;

const Overlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/55 backdrop-blur-[2px]",
      "motion-fade",
      className,
    )}
    {...props}
  />
));
Overlay.displayName = "DialogOverlay";
export const DialogOverlay = Overlay;

export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { hideClose?: boolean }
>(({ className, children, hideClose, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Inset from the edges on a phone, where a full-bleed dialog reads as a page.
        // Centred in, and capped to, the part of the screen an iOS keyboard leaves (`--kb`).
        "fixed left-1/2 top-[calc((100dvh-var(--kb,0px))/2)] z-50 grid w-[calc(100%-1.5rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4",
        // One column that may never grow past the dialog. A grid track sizes to its
        // content's min-content width by default, so one long line in a code block
        // or an unbroken path widened the whole dialog and scrolled it sideways,
        // instead of the block scrolling inside itself.
        "grid-cols-[minmax(0,1fr)]",
        "max-h-[calc(92dvh-var(--kb,0px))] overflow-y-auto overscroll-contain scrollbar-slim",
        "rounded-lg border border-border bg-elevated p-4 shadow-2xl sm:p-5",
        "motion-dialog",
        className,
      )}
      {...props}
      onPointerDownOutside={keepOpenForToasts(props.onPointerDownOutside)}
      onInteractOutside={keepOpenForToasts(props.onInteractOutside)}
    >
      {children}
      {hideClose ? null : (
        <DialogPrimitive.Close
          className="absolute right-3.5 top-3.5 rounded-sm p-1 text-muted-foreground opacity-70 transition-opacity hover:opacity-100 hover:bg-muted"
          aria-label="Close"
        >
          <X className="size-4" />
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = "DialogContent";

export const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col gap-1.5 pr-8", className)} {...props} />
);

export const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)} {...props} />
);

export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={cn("text-base font-semibold tracking-tight", className)} {...props} />
));
DialogTitle.displayName = "DialogTitle";

export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
DialogDescription.displayName = "DialogDescription";
